import { Classes } from "@blueprintjs/core";
import { useCallback, useMemo } from "react";
import styled from "styled-components";

import { Dimension } from "@superblocksteam/shared";

import { selectWidgets } from "legacy/actions/widgetActions";
import {
  GridDefaults,
  WidgetLabelPosition,
} from "legacy/constants/WidgetConstants";
import { APP_MODE } from "legacy/reducers/types";
import {
  selectGeneratedTheme,
  selectGeneratedThemeTypographies,
} from "legacy/selectors/themeSelectors";
import type { GeneratedTheme } from "legacy/themes";
import { CLASS_NAMES } from "legacy/themes/classnames";
import { useAppDispatch, useAppSelector } from "store/helpers";
import { getComponentDimensions } from "utils/size";

import {
  createCssVariablesForBorderWidthAndColorWithHover,
  generateCssForBorderWidthAndColorWithHover,
} from "../base/generateBorderCss";
import { generateBorderRadiusStyleObject } from "../base/generateBorderStyle";
import { getInputIconSize } from "../shared";
import { generateInputBorderOnHoverColor } from "../Shared/InputUtils";
import { labelStyle } from "../Shared/widgetLabelStyles";
import { useStyleClassNames, useTypographyStyling } from "../typographyHooks";
import { getLineHeightInPxFromTextStyle } from "../typographyUtils";
import {
  DEFAULT_DATEPICKER_INPUT_STYLE_VARIANT,
  DEFAULT_DATEPICKER_LABEL_STYLE_VARIANT,
} from "./Constants";
import DatePickerComponent, {
  INPUT_ADDITIONAL_MIN_HEIGHT,
  LABEL_EXTRA_HEIGHT_MARGIN,
} from "./DatePickerComponent";
import type { DatePickerComponentWithLayoutsManagedProps } from "./types";

const InputStyleOverrides = styled.div<{
  $height: number;
  hasLabel?: boolean;
}>`
  &&&& .${Classes.INPUT} {
    height: 100%;
  }

  ${generateCssForBorderWidthAndColorWithHover({
    selectors: [`&&&& .${Classes.INPUT}`],
  })}

  &&& label {
    ${labelStyle.vertical}
  }

  &&& .${CLASS_NAMES.DATEPICKER} {
    margin-top: 0;
  }

  overflow-y: hidden;
`;

export const DatePickerComponentWithLayoutManaged = (
  props: DatePickerComponentWithLayoutsManagedProps,
) => {
  const theme = useAppSelector(selectGeneratedTheme);
  const appMode = props.appMode;
  const widgetId = props.widgetId;
  const dispatch = useAppDispatch();

  const typographies = useAppSelector(selectGeneratedThemeTypographies);

  const labelProps = useTypographyStyling({
    textStyle: props.labelProps?.textStyle,
    defaultTextStyleVariant: DEFAULT_DATEPICKER_LABEL_STYLE_VARIANT,
    applyClassNameStylesToStyle: true,
  });

  const inputTypographyProps = useTypographyStyling({
    textStyle: props.inputProps?.textStyle,
    defaultTextStyleVariant: DEFAULT_DATEPICKER_INPUT_STYLE_VARIANT,
    applyClassNameStylesToStyle: true,
  });

  const labelClass = useStyleClassNames({
    textStyleVariant: labelProps.textStyleVariant, // Has fallback in place
    isLoading: props.isLoading,
    isDisabled: props.isDisabled,
    type: "label",
  });

  const inputClass = useStyleClassNames({
    textStyleVariant: inputTypographyProps?.textStyleVariant, // Has fallback in place
    isLoading: props.isLoading,
    isDisabled: props.isDisabled,
    type: "input",
  });

  // inputProps.textStyleVariant already has the the default fallback in place.
  // This method still requires it.
  const inputLineHeightPx = getLineHeightInPxFromTextStyle({
    textStyleVariant: inputTypographyProps?.textStyleVariant,
    nestedProps: props.inputProps?.textStyle,
    defaultTextStyleVariant: DEFAULT_DATEPICKER_INPUT_STYLE_VARIANT,
    typographies,
  });

  const inputBackgroundColor = props.inputProps?.backgroundColor;

  const inputBaseStyle = useMemo(() => {
    const vars = createCssVariablesForBorderWidthAndColorWithHover({
      border: props.inputProps?.border,
      fallbackBorderColor: theme.colors.neutral100,
      defaultBorderWidth: theme.defaultBorder.borderWidth,
      borderColorOnHover: generateInputBorderOnHoverColor(
        props.inputProps?.border,
        theme.colors.primary500,
        theme.colors,
      ),
    });

    const borderRadiusStyle = generateBorderRadiusStyleObject({
      borderRadius: props.inputProps?.borderRadius,
    });

    return inputBackgroundColor
      ? { backgroundColor: inputBackgroundColor, ...vars, ...borderRadiusStyle }
      : { ...vars, ...borderRadiusStyle };
  }, [props.inputProps, inputBackgroundColor, theme]);

  const { componentWidth } = getComponentDimensions(props);

  const iconSize = useMemo(
    () =>
      getInputIconSize({
        width: props.width,
        componentWidth: componentWidth,
        textStyle: props.inputProps?.textStyle,
        generatedTheme: theme,
      }),
    [props.width, componentWidth, props.inputProps?.textStyle, theme],
  );

  const { componentHeight } = getComponentDimensions(props);
  const hasLabel = Boolean(props.label?.trim?.().length);
  const dateValue = props.selectedDate || props.defaultDate;
  const isInvalid = !props.isValid && (props.isTouched || !!dateValue);

  const onInputClick = useCallback(() => {
    if (appMode === APP_MODE.EDIT && widgetId) {
      dispatch(selectWidgets([widgetId], false));
    }
  }, [appMode, widgetId, dispatch]);

  return (
    <InputStyleOverrides $height={componentHeight} hasLabel={hasLabel}>
      <DatePickerComponent
        label={props.label}
        dateFormat={props.dateFormat}
        displayDateFormat={props.displayDateFormat}
        widgetId={props.widgetId}
        isDisabled={props.isDisabled}
        datePickerType={props.datePickerType}
        onDateSelected={props.onDateSelected}
        selectedDate={props.selectedDate ?? props.defaultDate}
        isLoading={props.isLoading}
        minDate={props.minDate}
        maxDate={props.maxDate}
        isValid={!isInvalid}
        isTouched={props.isTouched}
        isRequired={props.isRequired}
        twentyFourHourTime={props.twentyFourHourTime}
        displayTimezone={
          props.manageTimezone ? props.displayTimezone : undefined
        }
        timezone={props.manageTimezone ? props.timezone : undefined}
        showTimezone={props.manageTimezone}
        showCalendarIcon={props.showIcon}
        onDatePickerClosed={props.onDatePickerClosed}
        labelClassName={labelClass}
        labelStyleOverride={labelProps?.style}
        inputClassName={inputClass}
        inputStyleOverride={{
          ...(inputTypographyProps?.style ?? {}),
          ...inputBaseStyle,
        }}
        inputLineHeightPx={inputLineHeightPx}
        labelPosition={props.labelProps?.position ?? WidgetLabelPosition.TOP}
        labelWidth={props.labelProps?.width}
        errorMessagePlacement={props.errorMessagePlacement}
        errorMessages={props.errorMessages}
        onInputClick={onInputClick}
        iconSize={iconSize}
        showError={props.showError}
        isFitContentHeight={props.height?.mode === "fitContent"}
      />
    </InputStyleOverrides>
  );
};

export const estimateInitialDatepickerWidgetHeightGU = (
  theme?: GeneratedTheme,
): number => {
  if (!theme?.typographies) {
    return 5;
  }

  const labelHeight = getLineHeightInPxFromTextStyle({
    textStyleVariant: DEFAULT_DATEPICKER_LABEL_STYLE_VARIANT,
    nestedProps: undefined,
    typographies: theme.typographies,
    defaultTextStyleVariant: DEFAULT_DATEPICKER_LABEL_STYLE_VARIANT,
  });

  const inputHeight = getLineHeightInPxFromTextStyle({
    textStyleVariant: DEFAULT_DATEPICKER_INPUT_STYLE_VARIANT,
    nestedProps: undefined,
    typographies: theme.typographies,
    defaultTextStyleVariant: DEFAULT_DATEPICKER_INPUT_STYLE_VARIANT,
  });

  const totalHeightPx =
    labelHeight +
    LABEL_EXTRA_HEIGHT_MARGIN +
    inputHeight +
    INPUT_ADDITIONAL_MIN_HEIGHT;

  return Dimension.toGridUnit(
    Dimension.px(totalHeightPx),
    GridDefaults.DEFAULT_GRID_ROW_HEIGHT,
  ).roundUp().value;
};
